import { useEffect, useId, useRef, useState } from 'react';
import { MEALS, MEAL_TITLE, type Meal } from '@jungles/shared';

/** Small form popover sharing the brand picker's palette and 44px touch rows. */
export function MealPicker({ value, onChange }: { value: Meal; onChange: (meal: Meal) => void }) {
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const options = useRef<(HTMLButtonElement | null)[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const close = (restore = true) => { setOpen(false); if (restore) trigger.current?.focus(); };
  const show = (index = MEALS.indexOf(value)) => { setActive(Math.max(0, index)); setOpen(true); };
  useEffect(() => {
    if (!open) return;
    options.current[active]?.focus();
  }, [open, active]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  return <span ref={root} className="meal-picker" onBlur={event => {
    if (open && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
  }}>
    <button ref={trigger} className="food-meal" type="button" aria-label={`Приём пищи: ${MEAL_TITLE[value]}`} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => open ? close() : show()} onKeyDown={event => {
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); show(event.key === 'Home' ? 0 : event.key === 'End' ? MEALS.length - 1 : MEALS.indexOf(value));
        }
      }}>{MEAL_TITLE[value]} <span className="select-chevron" aria-hidden="true" /></button>
    {open ? <span id={id} role="listbox" aria-label="Приём пищи" className="meal-options" onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key === 'Home' || event.key === 'End' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(index => event.key === 'Home' ? 0 : event.key === 'End' ? MEALS.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + MEALS.length) % MEALS.length);
      }
    }}>{MEALS.map((meal, index) => <button key={meal} ref={element => { options.current[index] = element; }} role="option" aria-selected={value === meal} tabIndex={active === index ? 0 : -1} type="button"
      onClick={() => { onChange(meal); close(); }}>{MEAL_TITLE[meal]}{meal === value ? <span aria-hidden="true">✓</span> : null}</button>)}</span> : null}
  </span>;
}
